import { Injectable } from '@angular/core';

export interface AudioCompressResult {
  file: File;
  /**
   * duration in seconds rounded to nearest integer
   * duration: Math.round(duration)
   */
  duration: number;
  /** Exact duration in seconds (raw float) for archival precision */
  exactDuration: number;
  originalSize: number; // bytes
  originalFormat: string; // mime type
  compressedSize: number; // bytes
  bitrate: number;
  channels: number;
}

export const AUDIO_MAX_DURATION_SECONDS = 180; // 3 minutes
export const AUDIO_MAX_PER_MEMORIAL = 5;

@Injectable({ providedIn: 'root' })
export class AudioCompressorService {
  /**
   * Reads the duration of an audio file without compressing it.
   * Use this for fast pre-validation before compression.
   */
  async getDuration(file: File): Promise<number> {
    const { Input, ALL_FORMATS, BlobSource } = await import('mediabunny');

    const input = new Input({
      formats: ALL_FORMATS,
      source: new BlobSource(file),
    });
    return input.computeDuration();
  }

  /**
   * Compresses an audio file to WebM/Opus at 32kbps mono.
   * - Validates duration <= AUDIO_MAX_DURATION_SECONDS before processing.
   * - Reports progress via onProgress callback (0 to 1).
   * - Returns a File with mime type audio/webm ready for upload.
   */
  async compress(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<AudioCompressResult> {
    const { Input, ALL_FORMATS, BlobSource, Output, WebMOutputFormat, BufferTarget, Conversion } =
      await import('mediabunny');

    // 1. Read duration fast — before doing any heavy work
    const input = new Input({
      formats: ALL_FORMATS,
      source: new BlobSource(file),
    });

    const duration = await input.computeDuration();

    if (duration > AUDIO_MAX_DURATION_SECONDS) {
      throw new Error(`DURATION_EXCEEDED:${Math.round(duration)}`);
    }

    // 2. Set up output: WebM container with Opus codec
    const output = new Output({
      format: new WebMOutputFormat(),
      target: new BufferTarget(),
    });

    // 3. Initialize conversion: audio-only, mono, 32kbps, trim to max duration
    const conversion = await Conversion.init({
      input,
      output,
      // Discard all video tracks if any exist (e.g. MP4 with embedded video)
      video: () => ({ discard: true }),
      audio: () => ({
        numberOfChannels: 1, // mono — sufficient for voice
        bitrate: 32_000, // 32 kbps Opus — ~240KB/min
      }),
      // Safety trim: never exceed 3 minutes even if metadata duration was wrong
      trim: { start: 0, end: AUDIO_MAX_DURATION_SECONDS },
    });

    if (!conversion.isValid) {
      throw new Error('CONVERSION_UNSUPPORTED');
    }

    // 4. Wire progress callback
    if (onProgress) {
      conversion.onProgress = onProgress;
    }

    // 5. Execute
    await conversion.execute();

    // 6. Build the output File
    const buffer = output.target.buffer!;
    const blob = new Blob([buffer], { type: 'audio/webm' });
    const baseName = file.name.split('.')[0];
    const compressedFile = new File([blob], `${baseName}.webm`, {
      type: 'audio/webm',
      lastModified: Date.now(),
    });

    return {
      file: compressedFile,
      duration: Math.round(duration), // seconds, rounded to nearest integer
      exactDuration: duration, // exact float duration
      originalSize: file.size,
      originalFormat: file.type,
      compressedSize: compressedFile.size,
      bitrate: 32000,
      channels: 1,
    };
  }

  /** Formats seconds as MM:SS */
  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
